import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { API, getAuthToken, setAuthToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Auth-header helper for raw `fetch` calls ──────────────────────
  // We can't use the shared axios interceptor for /auth/me because it's
  // fetched via native fetch here. Mirror the same dual-auth logic so
  // Safari users (cookie may be dropped by ITP) still authenticate via
  // the Bearer fallback in localStorage.
  const authedFetch = useCallback((path, init = {}) => {
    const t = getAuthToken();
    const headers = { ...(init.headers || {}) };
    if (t && !headers.Authorization) headers.Authorization = `Bearer ${t}`;
    return fetch(`${API}${path}`, { credentials: "include", ...init, headers });
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const r = await authedFetch("/auth/me");
      if (!r.ok) throw new Error("not auth");
      const u = await r.json();
      setUser(u);
    } catch (_) {
      setUser(null);
      // Drop any stale token so the next request doesn't replay a
      // revoked session. The cookie is server-set, so we leave it
      // alone — the browser will clear it on its own once expired.
      setAuthToken(null);
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    // If we're processing an OAuth callback, AuthCallback stores the Bearer
    // fallback first. Skip the /me check here so we don't 401 prematurely.
    if (window.location.hash?.includes("session_token=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    await authedFetch("/auth/logout", { method: "POST" });
    setUser(null);
    setAuthToken(null);
  }, [authedFetch]);

  // Login surfaces (demo-login, OAuth-callback, future password flow)
  // call this to mark the user as authed AND persist the Bearer fallback
  // token. The cookie is server-set in the same response.
  const acceptLogin = useCallback((payload) => {
    if (payload?.session_token) setAuthToken(payload.session_token);
    setUser(payload);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, checkAuth, setUser, logout, acceptLogin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
